import { IndexedDbStorageEngine } from '../engines/indexeddb-storage.engine';
import { LocalStorageEngine } from '../engines/local-storage.engine';
import { SessionStorageEngine } from '../engines/session-storage.engine';
import { StorageEngine } from '../types/storage-engine.enum';
import type {
	AsyncStorageEngine,
	SyncStorageEngine
} from '../types/storage-engine.interface';
import type {
	StorageApi,
	StorageEngineInstance,
	StorageEngineRegistry
} from '../types/storage-types';

export class StorageController implements StorageApi {
	private engine: StorageEngine;

	private readonly engines: StorageEngineRegistry = {
		[StorageEngine.Local]: new LocalStorageEngine(),
		[StorageEngine.Session]: new SessionStorageEngine(),
		[StorageEngine.IndexedDb]: new IndexedDbStorageEngine()
	};

	constructor(engine: StorageEngine = StorageEngine.Local) {
		this.engine = engine;
	}

	public setEngine(engine: StorageEngine): void {
		this.engine = engine;
	}

	public clear(engine?: StorageEngine): void {
		this.resolveSyncEngine(engine)?.clear();
	}

	public clearExcept(keys: string[], engine?: StorageEngine): void {
		this.resolveSyncEngine(engine)?.clearExcept(keys);
	}

	public get<T = unknown>(key: string, engine?: StorageEngine): T | null {
		const resolvedEngine = this.resolveSyncEngine(engine);
		return resolvedEngine?.get<T>(key) ?? null;
	}

	public getRaw(key: string, engine?: StorageEngine): string | null {
		const resolvedEngine = this.resolveSyncEngine(engine);
		return resolvedEngine?.getRaw(key) ?? null;
	}

	public set<T = unknown>(key: string, value: T, engine?: StorageEngine): void {
		this.resolveSyncEngine(engine)?.set<T>(key, value);
	}

	public remove(key: string, engine?: StorageEngine): void {
		this.resolveSyncEngine(engine)?.remove(key);
	}

	public getAsync<T = unknown>(key: string): Promise<T | null> {
		const engine = this.resolveAsyncEngine();
		return engine ? engine.getAsync<T>(key) : Promise.resolve(null);
	}

	public async setAsync<T = unknown>(key: string, value: T): Promise<void> {
		const engine = this.resolveAsyncEngine();

		if (!engine) {
			return;
		}

		await engine.setAsync<T>(key, value);
	}

	public async removeAsync(key: string): Promise<void> {
		const engine = this.resolveAsyncEngine();

		if (!engine) {
			return;
		}

		await engine.removeAsync(key);
	}

	private resolveEngine(engine?: StorageEngine): StorageEngineInstance {
		return this.engines[engine ?? this.engine];
	}

	private resolveSyncEngine(engine?: StorageEngine): SyncStorageEngine | null {
		const resolvedEngine = this.resolveEngine(engine);
		return this.isSyncStorageEngine(resolvedEngine) ? resolvedEngine : null;
	}

	private resolveAsyncEngine(): AsyncStorageEngine | null {
		const engine = this.resolveEngine();
		return this.isAsyncStorageEngine(engine) ? engine : null;
	}

	private isSyncStorageEngine(
		engine: StorageEngineInstance
	): engine is SyncStorageEngine {
		return 'get' in engine && 'set' in engine && 'remove' in engine;
	}

	private isAsyncStorageEngine(
		engine: StorageEngineInstance
	): engine is AsyncStorageEngine {
		return (
			'getAsync' in engine && 'setAsync' in engine && 'removeAsync' in engine
		);
	}
}
